import { Button, CircularProgress, Typography } from '@mui/material'
import { createFileRoute, Navigate } from '@tanstack/react-router'
import { useContext } from 'react'
import RbacStatusPanel from '~/components/communication/RbacStatusPanel'
import { allNavLinks } from '~/components/new/navigation/nav-links'
import { useNavItemVisibility } from '~/components/new/navigation/useNavItemVisible'
import { useFeatureFlagsReady } from '~/hooks/useFeatureFlag'
import { resolveTabletStoreId } from '~/utils/tablet-account'
import { AuthContext } from '../utils/context/auth.context'

export const Route = createFileRoute('/')({
  component: IndexComponentPage,
})

function IndexComponentPage() {
  const { authenticated, isLoading, logout, rbacGrantsV2 } = useContext(AuthContext)
  const isNavItemVisible = useNavItemVisibility()
  const flagsReady = useFeatureFlagsReady()

  if (isLoading) {
    return <CircularProgress />
  }

  // Un compte de pointage (« tablette ») atterrit sur l'écran de pointage de SA boutique, déduit de
  // ses grants `hr.clocking.clocking` : c'est ici qu'arrive la connexion (`signIn`). Aucun flag
  // n'y entre, la redirection n'attend donc pas Flipt.
  const tabletStoreId = authenticated ? resolveTabletStoreId({ grantsV2: rbacGrantsV2 }) : null
  if (tabletStoreId !== null) {
    return (
      <Navigate to="/workshift-attendance/$storeId" params={{ storeId: tabletStoreId }} replace />
    )
  }

  // La visibilité des entrées du menu dépend de leurs flags : tant que Flipt s'initialise, une
  // entrée flaggée est invisible et on atterrirait sur la mauvaise page (voire sur « Aucune page
  // accessible » pour un admin dont toutes les entrées sont flaggées).
  if (!flagsReady) {
    return <CircularProgress />
  }

  if (!authenticated) {
    return <Navigate to="/login" replace />
  }

  // Sinon, la seule source de vérité est ce que l'admin peut réellement ouvrir. On atterrit sur la
  // première entrée visible du menu, dans l'ordre où le menu les affiche.
  const firstVisible = allNavLinks.find(isNavItemVisible)

  if (!firstVisible) {
    return (
      <RbacStatusPanel>
        <Typography variant="h4" className="mb-3! font-semibold">
          Aucune page accessible
        </Typography>
        <Typography className="mb-6! text-muted-foreground">
          Votre compte n'a aucune permission permettant d'ouvrir une page du back-office.
          Rapprochez-vous d'un administrateur pour vous faire attribuer un rôle.
        </Typography>

        {/* Seule sortie : sans page accessible, toute navigation ramène ici. */}
        <Button variant="contained" onClick={logout}>
          Déconnexion
        </Button>
      </RbacStatusPanel>
    )
  }

  return <Navigate {...firstVisible.linkOptions} replace />
}
